import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import GroupCodesService from "../../../../services/groupcodes/GroupCodesService";
import TablesService from "../../../../services/tables/TablesService";
import PageHead from "../../../layout/PageHead";
import {
    UserSwitchOutlined,
    PlusOutlined
} from "@ant-design/icons";
import { Button, Tabs } from "antd";
import { ROUTES } from "../../../../config/consts/routes";
import { SECTIONS } from "../../../../config/constants";
import { useSearchParams } from "react-router-dom";
import { UserManagement } from "./components/UserManagement";
import { RoleManagement } from "./components/RoleManagement";
import { useSelector } from "react-redux";
import UsersService from "../../../../services/admin/UsersService";
import { AxiosResponse } from "axios";
import styles from "./AccessManagement.module.css";
import ExcelAddinUsersService from "../../../../services/excel-addin/UsersService";
import RolesService from "../../../../services/excel-addin/RolesService";
import notificationUtil from "../../../shared/utils/NotificationsUtil";
import { NOTIFICATION_TYPES } from "../../../../config/constants";


const { TabPane } = Tabs;

const routes = [
    {
        path: ROUTES.general.main.route,
        breadcrumbName: SECTIONS.general.name,
    },
    {
        breadcrumbName: SECTIONS.excelAddin.name,
    },
    {
        path: ROUTES.excelAddin.excelSettings.route,
        breadcrumbName: ROUTES.excelAddin.excelSettings.name,
    },
];

const AccessManagement = () => {
    const { t, i18n } = useTranslation("translations");

    const [searchParams, setSearchParams] = useSearchParams();

    interface User {
        id: number;
        email: string;
        userName: string;
        userDescription: string;
        roles: Role[];
        filter: string;
    }

    interface Role {
        id: number;
        description: string;
        users: User[];
        filter: string;
    }

    //pageHead
    const [groupCodes, setGroupCodes] = useState<string[]>([]);
    const [entities, setEntities] = useState<string[]>([]);
    const [operations, setOperations] = useState<string[]>([]);

    const [selectedGroupCode, setSelectedGroupCode] = useState<number>();
    const [selectedEntity, setSelectedEntity] = useState<number>();
    const [selectedOperation, setSelectedOperation] = useState<number>();


    // users and roles
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);

    const [isTableLoading, setIsTableLoading] = useState<boolean>(true);

    const fetchData = () => {
        ExcelAddinUsersService.getAll().then(response => {
            setUsers(response.data);
        })
            .catch(error => notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false }))
            .finally(() => setIsTableLoading(false));

        RolesService.getAll().then(response => {
            setRoles(response.data);
        })
            .catch(error => notificationUtil.openNotificationDialog({ description: error.response.data, type: NOTIFICATION_TYPES.ERROR, logError: false }))
            .finally(() => setIsTableLoading(false));
    }

    //permissions
    const [editAllowed, setEditAllowed] = useState<boolean>(false);

    //drawer
    const [openNewUserDrawer, setOpenNewUserDrawer] = useState(false);
    const [openNewRoleDrawer, setOpenNewRoleDrawer] = useState(false);

    const { profile } = useSelector((state: any) => ({
        profile: state.profile,
    }));

    useEffect(() => {
        GroupCodesService.getAll().then((response: AxiosResponse) => {
            const data = response.data;
            setGroupCodes(data);
        });

        TablesService.getAllEntities().then((response: AxiosResponse) => {
            const data = response.data;
            setEntities(data);
        });

        TablesService.getAllOperations().then((response: AxiosResponse) => {
            const data = response.data;
            setOperations(data);
        });

        fetchData();
    }, []);

    useEffect(() => {
        UsersService.permissions(profile.id)
            .then((response) => {
                setEditAllowed(response.data.isAdmin);
            })
    }, [profile])


    const onChange = (key: string) => {
        searchParams.set("tab", key);
        setSearchParams(searchParams);
    };

    const onNewUserButtonClick = () => {
        setOpenNewUserDrawer(true);
    };

    const onNewRoleButtonClick = () => {
        setOpenNewRoleDrawer(true);
    };

    const userManagementActionButton = (
        <Button
            type={"primary"}
            onClick={onNewUserButtonClick}
            className="actionButton"
            hidden={!editAllowed}
        >
            <PlusOutlined />
            NEW USER
        </Button>
    );

    const roleManagementActionButton = (
        <Button
            type={"primary"}
            onClick={onNewRoleButtonClick}
            className="actionButton"
            hidden={!editAllowed}
        >
            <PlusOutlined />
            NEW ROLE
        </Button>
    );

    return (
        <div>
            <PageHead
                title={t("Page.ExcellAddin.Headers.AccessManagement", {
                    lngs: i18n.languages,
                    defaultValue: "Access Management",
                })}
                icon={<UserSwitchOutlined />}
                routes={routes}
                groupCodes={groupCodes}
                entities={entities}
                operations={operations}
                selectedGroupCode={selectedGroupCode}
                selectedEntity={selectedEntity}
                selectedOperation={selectedOperation}
                setSelectedGroupCode={setSelectedGroupCode}
                setSelectedEntity={setSelectedEntity}
                setSelectedOperation={setSelectedOperation}
                setSelectGroupCodeDisabled={true}
                setSelectEntityDisabled={true}
                setSelectOperationDisabled={true}
                mainAction={
                    searchParams.get("tab") &&
                        searchParams.get("tab") === "user-management"
                        ? userManagementActionButton
                        : searchParams.get("tab") &&
                            searchParams.get("tab") === "role-management"
                            ? roleManagementActionButton
                            : true
                }
                otherActions={[]}
                setActionsDisabled={false}
            />
            <div className="card-container">
                <Tabs
                    type="card"
                    activeKey={searchParams.get("tab") ? searchParams.get("tab")! : ""}
                    onTabClick={onChange}
                    className={styles.fullWidth}
                >
                    <TabPane
                        tab={t("Page.ExcelAddin.Headers.UserManagement", {
                            lngs: i18n.languages,
                            defaultValue: "User management",
                        })}
                        key="user-management"
                    >
                        <UserManagement openNewUserDrawer={openNewUserDrawer} setOpenNewUserDrawer={setOpenNewUserDrawer} editAllowed={editAllowed} data={users} setData={setUsers} roles={roles} fetchData={fetchData} isTableLoading={isTableLoading} />
                    </TabPane>
                    <TabPane
                        tab={t("Page.ExcelAddin.Headers.RoleManagement", {
                            lngs: i18n.languages,
                            defaultValue: "Role management",
                        })}
                        key="role-management"
                    >
                        <RoleManagement setOpenNewRoleDrawer={setOpenNewRoleDrawer} openNewRoleDrawer={openNewRoleDrawer} editAllowed={editAllowed} data={roles} setData={setRoles} users={users} fetchData={fetchData} isTableLoading={isTableLoading} />
                    </TabPane>
                </Tabs>
            </div>
        </div>
    );
};

export { AccessManagement };